// src/pages/SignupPage.tsx - User Signup Page
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Fade,
  Chip,
  Grid,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Person,
  Email,
  Lock,
  Phone,
  Work,
  Business,
  LinkedIn,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Visibility,
  VisibilityOff,
  TrendingUp,
  Analytics,
  Security,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { api } from '@services';

const expertiseAreas = [
  'Hotel Valuation',
  'Real Estate Investment',
  'Hotel Operations',
  'Asset Management',
  'Market Analysis',
  'Financial Modeling',
  'Due Diligence',
  'Hotel Development',
];

const steps = ['Personal Information', 'Professional Details'];

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
    jobTitle: '',
    company: '',
    yearsOfExperience: '',
    certifications: [] as string[],
    areasOfExpertise: [] as string[],
    linkedinProfile: '',
    preferredContact: 'email',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: 'en',
    notificationSettings: {
      email: true,
      push: true
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent<string[]>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only submit if we're on the final step
    if (currentStep !== steps.length - 1) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Sending signup data:', formData);
      const response = await api.post('/auth/signup', formData);
      console.log('Signup response:', response.data);

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);

        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        try {
          const user = await register(formData);
          console.log('User registered successfully:', user);
          navigate('/dashboard');
        } catch (registerError) {
          console.error('Registration context error:', registerError);
          navigate('/dashboard');
        }
      } else {
        throw new Error('No token received from server');
      }
    } catch (error: any) {
      console.error('Signup error:', error);

      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const validationErrors = error.response.data.details
          .map((detail: any) => `${detail.field}: ${detail.message}`)
          .join(', ');
        setError(`Validation failed: ${validationErrors}`);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.message === 'Network Error') {
        setError('Unable to connect to the server. Please check your network connection and make sure the backend is running.');
      } else if (error.code === 'ECONNREFUSED') {
        setError('Backend server is not running. Please start the backend and try again.');
      } else {
        setError(error.message || 'Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isNextDisabled = () => {
    if (currentStep === 0) {
      return !formData.name || !formData.email || !formData.password;
    }
    return false;
  };

  const renderFormField = (
    label: string,
    children: React.ReactNode,
    required = false
  ) => (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
          color: '#1a365d',
          fontWeight: 700,
          fontSize: '0.95rem',
        }}
      >
        {label} {required && <span style={{ color: theme.palette.error.main }}>*</span>}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.4,
          background: `
            radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, ${alpha(theme.palette.info.main, 0.05)} 0%, transparent 50%)
          `,
        }}
      />

      {/* Back to Landing Button */}
      <IconButton
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          color: theme.palette.text.secondary,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
          '&:hover': {
            backgroundColor: 'white',
            color: theme.palette.primary.main,
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <ArrowBack />
      </IconButton>

      <Grid container sx={{ minHeight: '100vh' }}>
        {/* Left Side - Branding */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="sm">
            <Box sx={{ textAlign: 'center', px: 4 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 20px 40px rgba(26, 54, 93, 0.3)',
                }}
              >
                <Business sx={{ fontSize: 50, color: 'white' }} />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', md: '2.75rem' },
                }}
              >
                Join Valuate
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: '#475569',
                  mb: 4,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                Start Your Professional Hotel Valuation Journey
              </Typography>

              <Stack spacing={2.5}>
                {[
                  { icon: TrendingUp, text: 'Access Premium Market Data' },
                  { icon: Analytics, text: 'AI-Powered Valuation Tools' },
                  { icon: Security, text: 'Secure Professional Platform' },
                ].map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <feature.icon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#475569' }}>
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Container>
        </Grid>

        {/* Right Side - Signup Form */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="md">
            <Fade in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  mx: { xs: 2, sm: 3 },
                  my: { xs: 4, md: 0 },
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                  {/* Mobile Logo */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 4 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: '0 8px 24px rgba(26, 54, 93, 0.3)',
                      }}
                    >
                      <Business sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Join Valuate
                    </Typography>
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: '#1a365d',
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: { xs: '1.75rem', md: '2rem' },
                    }}
                  >
                    Create Your Account
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#64748b',
                      mb: 4,
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: '1.05rem',
                      fontWeight: 500,
                    }}
                  >
                    Join thousands of hotel investment professionals
                  </Typography>

                  {/* Progress Stepper */}
                  <Box sx={{ mb: 4 }}>
                    <Stepper activeStep={currentStep} alternativeLabel>
                      {steps.map((label, index) => (
                        <Step key={label}>
                          <StepLabel
                            sx={{
                              '& .MuiStepLabel-label': {
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                '&.Mui-active': {
                                  color: theme.palette.primary.main,
                                },
                                '&.Mui-completed': {
                                  color: theme.palette.success.main,
                                },
                              },
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: 'none',
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        '& .MuiAlert-message': {
                          fontWeight: 500,
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    {currentStep === 0 ? (
                      // Step 1: Personal Information
                      <Stack spacing={3}>
                        {renderFormField('Full Name', (
                          <TextField
                            fullWidth
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            InputProps={{
                              startAdornment: <Person sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.8),
                                '& fieldset': {
                                  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                transition: 'all 0.2s ease-in-out',
                              },
                              '& .MuiInputBase-input': {
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#1a202c !important',
                                '&::placeholder': {
                                  color: '#a0aec0 !important',
                                  opacity: 1,
                                },
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                              '& input': {
                                color: '#1a202c !important',
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                            }}
                          />
                        ), true)}

                        {renderFormField('Email Address', (
                          <TextField
                            fullWidth
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email address"
                            InputProps={{
                              startAdornment: <Email sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.8),
                                '& fieldset': {
                                  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                transition: 'all 0.2s ease-in-out',
                              },
                              '& .MuiInputBase-input': {
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#1a202c !important',
                                '&::placeholder': {
                                  color: '#a0aec0 !important',
                                  opacity: 1,
                                },
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                              '& input': {
                                color: '#1a202c !important',
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                            }}
                          />
                        ), true)}

                        {renderFormField('Password', (
                          <TextField
                            fullWidth
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            required
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Create a secure password"
                            InputProps={{
                              startAdornment: <Lock sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                              endAdornment: (
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  size="small"
                                  sx={{
                                    mr: 0.5,
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                      color: theme.palette.primary.main,
                                    },
                                  }}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.8),
                                '& fieldset': {
                                  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                transition: 'all 0.2s ease-in-out',
                              },
                              '& .MuiInputBase-input': {
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#1a202c !important',
                                '&::placeholder': {
                                  color: '#a0aec0 !important',
                                  opacity: 1,
                                },
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                              '& input': {
                                color: '#1a202c !important',
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                            }}
                          />
                        ), true)}

                        {renderFormField('Phone Number', (
                          <TextField
                            fullWidth
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            placeholder="Enter your phone number"
                            InputProps={{
                              startAdornment: <Phone sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.8),
                                '& fieldset': {
                                  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                transition: 'all 0.2s ease-in-out',
                              },
                              '& .MuiInputBase-input': {
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#1a202c !important',
                                '&::placeholder': {
                                  color: '#a0aec0 !important',
                                  opacity: 1,
                                },
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                              '& input': {
                                color: '#1a202c !important',
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      // Step 2: Professional Information
                      <Stack spacing={3}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            {renderFormField('Job Title', (
                              <TextField
                                fullWidth
                                name="jobTitle"
                                value={formData.jobTitle}
                                onChange={handleChange}
                                placeholder="e.g., Investment Analyst"
                                InputProps={{
                                  startAdornment: <Work sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.background.default, 0.8),
                                    '& fieldset': {
                                      border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main,
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main,
                                      borderWidth: '2px',
                                    },
                                    '&.Mui-focused': {
                                      backgroundColor: 'white',
                                      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                  },
                                  '& .MuiInputBase-input': {
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: '#1a202c !important',
                                    '&::placeholder': {
                                      color: '#a0aec0 !important',
                                      opacity: 1,
                                    },
                                    '&:focus': {
                                      color: '#1a202c !important',
                                    },
                                  },
                                  '& input': {
                                    color: '#1a202c !important',
                                    '&:focus': {
                                      color: '#1a202c !important',
                                    },
                                  },
                                }}
                              />
                            ))}
                          </Grid>

                          <Grid item xs={12} md={6}>
                            {renderFormField('Company', (
                              <TextField
                                fullWidth
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder="Your company name"
                                InputProps={{
                                  startAdornment: <Business sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.background.default, 0.8),
                                    '& fieldset': {
                                      border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main,
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main,
                                      borderWidth: '2px',
                                    },
                                    '&.Mui-focused': {
                                      backgroundColor: 'white',
                                      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                  },
                                  '& .MuiInputBase-input': {
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: '#1a202c !important',
                                    '&::placeholder': {
                                      color: '#a0aec0 !important',
                                      opacity: 1,
                                    },
                                    '&:focus': {
                                      color: '#1a202c !important',
                                    },
                                  },
                                  '& input': {
                                    color: '#1a202c !important',
                                    '&:focus': {
                                      color: '#1a202c !important',
                                    },
                                  },
                                }}
                              />
                            ))}
                          </Grid>
                        </Grid>

                        {renderFormField('Years of Experience', (
                          <TextField
                            fullWidth
                            name="yearsOfExperience"
                            value={formData.yearsOfExperience}
                            onChange={handleChange}
                            placeholder="e.g., 5"
                            type="number"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.8),
                                '& fieldset': {
                                  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                transition: 'all 0.2s ease-in-out',
                              },
                              '& .MuiInputBase-input': {
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#1a202c !important',
                                '&::placeholder': {
                                  color: '#a0aec0 !important',
                                  opacity: 1,
                                },
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                              '& input': {
                                color: '#1a202c !important',
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                            }}
                          />
                        ))}

                        {renderFormField('Areas of Expertise', (
                          <FormControl fullWidth>
                            <Select
                              multiple
                              name="areasOfExpertise"
                              value={formData.areasOfExpertise}
                              onChange={handleSelectChange}
                              displayEmpty
                              renderValue={(selected) => {
                                if (selected.length === 0) {
                                  return (
                                    <Typography sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                                      Select your areas of expertise
                                    </Typography>
                                  );
                                }
                                return (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map((value) => (
                                      <Chip
                                        key={value}
                                        label={value}
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                          color: theme.palette.primary.main,
                                          fontWeight: 500,
                                        }}
                                      />
                                    ))}
                                  </Box>
                                );
                              }}
                              sx={{
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.8),
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                '& .MuiSelect-select': {
                                  py: 1.5,
                                  fontSize: '1rem',
                                  fontWeight: 500,
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              {expertiseAreas.map((area) => (
                                <MenuItem key={area} value={area}>
                                  {area}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ))}

                        {renderFormField('LinkedIn Profile', (
                          <TextField
                            fullWidth
                            name="linkedinProfile"
                            value={formData.linkedinProfile}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/your-profile"
                            InputProps={{
                              startAdornment: <LinkedIn sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.8),
                                '& fieldset': {
                                  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                transition: 'all 0.2s ease-in-out',
                              },
                              '& .MuiInputBase-input': {
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#1a202c !important',
                                '&::placeholder': {
                                  color: '#a0aec0 !important',
                                  opacity: 1,
                                },
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                              '& input': {
                                color: '#1a202c !important',
                                '&:focus': {
                                  color: '#1a202c !important',
                                },
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    )}

                    {/* Action Buttons */}
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={currentStep === 0 ? () => navigate('/login') : handleBack}
                        sx={{
                          borderRadius: 2,
                          px: 4,
                          py: 1.5,
                          borderColor: alpha(theme.palette.primary.main, 0.5),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderWidth: '2px',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            borderWidth: '2px',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        {currentStep === 0 ? 'Back to Login' : 'Previous'}
                      </Button>

                      {currentStep < steps.length - 1 ? (
                        <Button
                          type="button"
                          variant="contained"
                          onClick={handleNext}
                          disabled={isNextDisabled()}
                          endIcon={<ArrowForward />}
                          sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: '0 8px 32px rgba(26, 54, 93, 0.3)',
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 12px 40px rgba(26, 54, 93, 0.4)',
                            },
                            '&:active': {
                              transform: 'translateY(0px)',
                            },
                            '&:disabled': {
                              background: theme.palette.action.disabledBackground,
                              transform: 'none',
                              boxShadow: 'none',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          Continue
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={loading}
                          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                          sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 12px 40px rgba(34, 197, 94, 0.4)',
                            },
                            '&:active': {
                              transform: 'translateY(0px)',
                            },
                            '&:disabled': {
                              background: theme.palette.action.disabledBackground,
                              transform: 'none',
                              boxShadow: 'none',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          {loading ? 'Creating Account...' : 'Create Account'}
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  {/* Already have account link */}
                  <Box
                    sx={{
                      mt: 4,
                      p: 3,
                      textAlign: 'center',
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>
                      Already have an account?{' '}
                      <Button
                        variant="text"
                        onClick={() => navigate('/login')}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        Sign In
                      </Button>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SignupPage;
